import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { supabaseAdmin } from "@/integrations/supabase/client.server";

/** Erlaubt nur Weiterleitungen auf eigene Domains, verhindert Open-Redirect. */
function isAllowedRedirect(raw: string): boolean {
  try {
    const host = new URL(raw).hostname.toLowerCase();
    return (
      host === "omselected.art" ||
      host.endsWith(".omselected.art") ||
      host.endsWith(".lovable.app") ||
      host.endsWith(".lovable.dev")
    );
  } catch {
    return false;
  }
}

/**
 * Lädt einen freigeschalteten Visagisten per Admin-Invite ein.
 * Mirror von inviteApprovedPhotographer.
 */
export const inviteApprovedVisagist = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data) =>
    z
      .object({
        email: z.string().email().max(255),
        fullName: z.string().min(1).max(200),
        redirectTo: z.string().url().refine(isAllowedRedirect, {
          message: "redirectTo muss auf eine omselected.art-Domain zeigen",
        }),
      })
      .parse(data),
  )
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;

    const { data: allowed, error: roleErr } = await supabase.rpc(
      "is_admin_or_curator",
      { _user_id: userId },
    );
    if (roleErr || !allowed) {
      throw new Response("Forbidden", { status: 403 });
    }

    const { data: existingId } = await supabase.rpc("find_user_id_by_email", {
      _email: data.email,
    });
    if (existingId) {
      return { userId: existingId as string, invited: false };
    }

    const { data: invited, error: invErr } =
      await supabaseAdmin.auth.admin.inviteUserByEmail(data.email, {
        redirectTo: data.redirectTo,
        data: { display_name: data.fullName },
      });
    if (invErr || !invited?.user) {
      throw new Response(
        `Invite fehlgeschlagen: ${invErr?.message ?? "unbekannter Fehler"}`,
        { status: 500 },
      );
    }

    return { userId: invited.user.id, invited: true };
  });